"use client";

import { useState, useEffect } from 'react';
import Link from "next/link";
import { usePathname } from 'next/navigation';
import { Button } from "@/components/ui/button";
import { siteConfig } from "@/lib/siteConfig";
import {
    ArrowUpRight, ChevronDown, ChevronUp,
    CircleCheckBig, Calendar, UserPen
} from 'lucide-react';

export default function PageBanner({
    title, subtitle, listContent, bgImage, buttonText,
    bannerHeight, mobileResponsiveBannerHeight,
    breadcrumbs = [], recentPosts, postMeta, basePath = "/blog",
}) {
    const pathname = usePathname();

    const breadcrumbJsonLd = breadcrumbs.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((crumb, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: crumb.label,
            item: `${siteConfig.url}${crumb.href ?? pathname}`,
        })),
    } : null;
    const isBlog = Array.isArray(recentPosts) && recentPosts.length > 0;
    const total = isBlog ? recentPosts.length + 1 : 1;
    const [current, setCurrent] = useState(0);

    const handleScrollDown = () => {
        window.scrollTo({ top: window.innerHeight * 0.6, behavior: "smooth" });
    };

    const goNext = () => setCurrent(c => (c + 1) % total);
    const goPrev = () => setCurrent(c => (c - 1 + total) % total);

    // Auto scroll slider 5 seconds //
    useEffect(() => {
        if (!isBlog) return;
        const timer = setInterval(() => setCurrent(c => (c + 1) % total), 5000);
        return () => clearInterval(timer);
    }, [isBlog, total]);

    const formatDate = (str) => {
        if (!str) return '';
        return new Date(str).toLocaleDateString('en-US', {
            month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
        });
    };

    const currentBg =
        isBlog && current > 0 && recentPosts[current - 1]?.coverImage
            ? "/images/Blog-Banner.webp"
            : bgImage;

    const bannerHMobile = mobileResponsiveBannerHeight || (postMeta && basePath === '/case-studies' ? '440px' : '340px');
    const bannerHXl = bannerHeight || (isBlog ? '540px' : postMeta && basePath === '/case-studies' ? '440px' : '500px');
    const bannerHMd = `${Math.round((parseInt(bannerHMobile, 10) + parseInt(bannerHXl, 10)) / 2)}px`;

    return (
        <section
            className="relative w-full min-h-[var(--banner-h-mobile)] md:min-h-[var(--banner-h-md)] xl:min-h-[var(--banner-h-xl)] flex items-end overflow-hidden"
            style={{
                '--banner-h-mobile': bannerHMobile,
                '--banner-h-md': bannerHMd,
                '--banner-h-xl': bannerHXl,
            }}
            aria-label="Page banner"
        >
            {breadcrumbJsonLd && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
                />
            )}
            <div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-700"
                style={{ backgroundImage: `url(${currentBg})` }}
            />
            <div className="absolute inset-0 bg-gray-900/90" />

            {/* In normal flow (not absolute) so a long title grows the banner instead of overflowing under the fixed header */}
            <div className={`relative w-full flex items-end transition-opacity duration-500 ${current === 0 ? 'opacity-100 z-10 pointer-events-auto' : 'opacity-0 z-0 pointer-events-none'}`}>
                <div className={`w-full px-6 max-w-[1440px] mx-auto ${pathname === '/contact-us' || pathname === '/blog' || pathname === '/case-studies' || (postMeta && basePath !== '/case-studies') ? 'pt-28 md:pt-32 pb-16 md:pb-20' : pathname.startsWith('/store') ? 'pt-32 pb-22' : 'pt-28 md:pt-32 pb-22'}`}>
                    <h1 className={`font-bold text-white leading-tight ${postMeta ? 'max-w-4xl text-2xl sm:text-3xl md:text-4xl' : 'max-w-5xl text-2xl sm:text-3xl md:text-5xl'}`}>
                        {title}
                    </h1>
                    <div className="flex items-center gap-2 my-3 md:my-5">
                        <span className="block h-px w-12 bg-gray-500" />
                        <span className="block w-2 h-2 rounded-full bg-red-500" />
                        <span className="block h-px w-12 bg-gray-500" />
                    </div>
                    {subtitle && !postMeta && (
                        <p className="text-sm md:text-lg text-gray-300 max-w-3xl">{subtitle}</p>
                    )}

                    {/* Article detail page meta — shown only when postMeta prop is passed */}
                    {postMeta && (
                        <div className="flex flex-wrap items-center gap-3 text-gray-300 text-sm mt-4">
                            {postMeta.authorName && (
                                postMeta.authorSlug ? (
                                    <Link href={`${basePath}/author/${postMeta.authorSlug}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
                                        <UserPen size={14} className="text-primary shrink-0" />
                                        {postMeta.authorName}
                                    </Link>
                                ) : (
                                    <span className="flex items-center gap-1.5">
                                        <UserPen size={14} className="text-primary shrink-0" />
                                        {postMeta.authorName}
                                    </span>
                                )
                            )}
                            {postMeta.publishedAt && (
                                <span className="flex items-center gap-1.5">
                                    <Calendar size={14} className="text-primary shrink-0" />
                                    {formatDate(postMeta.publishedAt)}
                                </span>
                            )}
                            {/* Backward compat: single category string */}
                            {!postMeta.categories && postMeta.category && (
                                <span className="inline-block bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full tracking-wider">
                                    {postMeta.category}
                                </span>
                            )}
                        </div>
                    )}

                    {listContent && Array.isArray(listContent) && (
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 md:gap-y-3 mt-4 md:mt-6 max-w-4xl">
                            {listContent.map((item, index) => {
                                const isObj = item !== null && typeof item === 'object';
                                const match = !isObj ? item.match(/^(\d[\d,.]*[+%])\s(.+)$/) : null;
                                const stat = isObj ? item.stat : (match ? match[1] : null);
                                const label = isObj ? item.label : (match ? match[2] : item);
                                return (
                                    <li key={index} className="flex items-start gap-2 text-gray-300 text-sm md:text-base font-medium">
                                        <CircleCheckBig size={16} className="text-primary" />
                                        {stat && <span className="text-white font-bold">{stat}</span>}
                                        <span>{label}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {!isBlog && pathname !== '/contact-us' && !postMeta && (
                        <Link href="/contact-us">
                            <Button className="bg-red-gradient border-0 md:text-base py-4 md:py-5 px-4 mt-8 btn-glass group cursor-pointer">
                                <span className="relative text-sm z-10 flex items-center gap-2 text-white group-hover:text-secondary transition-colors duration-400">
                                    {buttonText ? buttonText : "Connect With Us"}
                                    <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                                </span>
                            </Button>
                        </Link>
                    )}
                </div>
            </div>

            {isBlog && recentPosts.map((post, i) => {
                const slideIndex = i + 1;
                const isActive = current === slideIndex;

                return (
                    <div
                        key={post.id}
                        className={`absolute inset-0 flex items-end transition-opacity duration-500 ${isActive ? 'opacity-100 z-10 pointer-events-auto' : 'opacity-0 z-0 pointer-events-none'}`}
                    >
                        <div className="w-full px-6 pb-16 pt-10 md:pb-24 md:pt-20 max-w-[1440px] mx-auto">
                            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white max-w-3xl leading-tight mb-3 md:mb-5">
                                {post.title.length > 56 ? post.title.slice(0, 56).trimEnd() + '…' : post.title}
                            </h2>
                            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-gray-300 text-xs md:text-sm mb-5 md:mb-8">
                                {post.author?.name && (
                                    <span className="flex items-center gap-1.5">
                                        <UserPen size={14} className="text-primary shrink-0" />
                                        {post.author.name}
                                    </span>
                                )}
                                {post.publishedAt && (
                                    <span className="flex items-center gap-1.5">
                                        <Calendar size={14} className="text-primary shrink-0" />
                                        {formatDate(post.publishedAt)}
                                    </span>
                                )}
                            </div>
                            <Link href={`${basePath}/${post.slug}`}>
                                <Button className="bg-red-gradient border-0 md:text-base py-4 md:py-5 px-4 btn-glass group cursor-pointer">
                                    <span className="relative text-sm z-10 flex items-center gap-2 text-white group-hover:text-secondary transition-colors duration-400">
                                        Read Article
                                        <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                                    </span>
                                </Button>
                            </Link>
                        </div>
                    </div>
                );
            })}

            {isBlog && (
                <>
                    <div className="hidden min-[1600px]:flex absolute left-14 top-1/2 -translate-y-1/2 z-20 flex-col gap-3">
                        <button
                            onClick={goPrev}
                            aria-label="Previous slide"
                            title='Previous'
                            className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 border border-white/20 flex items-center justify-center text-white transition-all duration-200 cursor-pointer"
                        >
                            <ChevronUp size={16} />
                        </button>
                        <button
                            onClick={goNext}
                            aria-label="Next slide"
                            title='Next'
                            className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 border border-white/20 flex items-center justify-center text-white transition-all duration-200 cursor-pointer"
                        >
                            <ChevronDown size={16} />
                        </button>
                    </div>

                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center">
                        {Array.from({ length: total }).map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setCurrent(i)}
                                aria-label={`Go to slide ${i + 1}`}
                                className="p-2 min-w-6 min-h-6 flex items-center justify-center cursor-pointer"
                            >
                                <span
                                    className={`block h-2 rounded-full transition-all duration-300 ${i === current ? 'w-6 bg-primary' : 'w-2 bg-white/40 hover:bg-white/70'}`}
                                />
                            </button>
                        ))}
                    </div>
                </>
            )}

            {(!isBlog || current === 0) && (
                <button
                    onClick={handleScrollDown}
                    aria-label="Scroll down"
                    className="hidden xl:flex absolute right-24 top-1/2 -translate-y-1/2 z-10 flex-col items-center gap-3 group cursor-pointer bg-transparent border-0 outline-none"
                >
                    <span
                        className="text-gray-400 text-[11px] tracking-[0.25em] uppercase group-hover:text-white transition-colors duration-200"
                        style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                    >
                        Scroll Down
                    </span>
                    <span className="block w-px h-16 bg-gradient-to-b from-gray-400 to-transparent" />
                    <ChevronDown size={16} className="text-gray-400 group-hover:text-white group-hover:translate-y-1 transition-all duration-200 animate-bounce" />
                </button>
            )}
        </section>
    );
}
